import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Plus, Power, Archive, Trash2, UserPlus, Stethoscope, Clock, X, Camera } from 'lucide-react';

export default function AdminClinicians() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editFor, setEditFor] = useState(null); // clinician being edited (photo/bio)
  const [form, setForm] = useState({
    email: '', password: '', name: '', credentials: '', photo_url: '', bio: '', active: true
  });

  const load = () => api.get('/clinicians').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onPhotoUpload = (e, setter) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1024 * 1024 * 2) { alert('Please keep the photo under 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setter(ev.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    try {
      await api.post('/clinicians', form);
      setShow(false);
      setForm({ email: '', password: '', name: '', credentials: '', photo_url: '', bio: '', active: true });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };
  const toggleActive = async (c) => { await api.put(`/clinicians/${c.user_id}`, { active: !c.active }); load(); };
  const archive = async (c) => { await api.put(`/clinicians/${c.user_id}`, { archived: !c.archived, active: false }); load(); };
  const remove = async (c) => {
    const msg = `Permanently delete ${c.name}?\n\nThis removes ${c.email}'s account, their appointments, availability and schedule blocks. Past visit notes keep their name as the therapist of record.\n\nThis CANNOT be undone. Use "Archive" instead if you only want to deactivate them.`;
    if (!(await safeConfirm(msg))) return;
    try {
      await api.delete(`/users/${c.user_id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const [hoursFor, setHoursFor] = useState(null);  // clinician being edited
  const toggleApproval = async (c) => {
    await api.put(`/clinicians/${c.user_id}`, { requires_approval: !c.requires_approval });
    load();
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">STAFF</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Healthcare providers</h1>
          <p className="text-fg-muted mt-2 text-sm">Add, activate/deactivate, or archive clinicians. Clinicians have limited access to patient charts, exercises and appointments.</p>
        </div>
        <button onClick={()=>setShow(!show)} className="btn-primary inline-flex items-center gap-2" data-testid="new-clin-btn">
          <UserPlus className="w-4 h-4"/> New clinician
        </button>
      </div>

      {show && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-3">New clinician account</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <F label="Full name"><input className="input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="clin-name"/></F>
            <F label="Credentials (e.g. PT, DPT, OCS)"><input className="input" value={form.credentials} onChange={e=>setForm({...form, credentials:e.target.value})} data-testid="clin-creds"/></F>
            <F label="Email"><input type="email" className="input" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="clin-email"/></F>
            <F label="Password (initial)"><input type="password" className="input" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="clin-pass"/></F>
            <F label="Photo (jpg/png ≤ 2 MB)" full>
              <div className="flex items-center gap-3">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="Preview" className="w-16 h-16 rounded-full object-cover border border-soft"/>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#F2EBDB] flex items-center justify-center">
                    <Camera className="w-5 h-5 text-fg-muted"/>
                  </div>
                )}
                <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1.5" data-testid="clin-photo-btn">
                  <Camera className="w-3.5 h-3.5"/> {form.photo_url ? 'Change photo' : 'Upload photo'}
                  <input type="file" accept="image/*" className="hidden"
                         onChange={(e) => onPhotoUpload(e, (val) => setForm(f => ({ ...f, photo_url: val })))}
                         data-testid="clin-photo-input"/>
                </label>
                {form.photo_url && (
                  <button onClick={() => setForm(f => ({ ...f, photo_url: '' }))} type="button"
                          className="text-xs text-[#B83A3A]" data-testid="clin-photo-clear">Remove</button>
                )}
              </div>
            </F>
            <F label="Bio" full><textarea className="input min-h-[160px]" maxLength={2000} value={form.bio} onChange={e=>setForm({...form, bio:e.target.value})} placeholder="Background, certifications, specialties…" data-testid="clin-bio"/></F>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={submit} disabled={!form.email || !form.password || !form.name} className="btn-primary" data-testid="clin-save">Create</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <div key={c.user_id} className={`bento-card ${c.archived ? 'opacity-60' : ''}`} data-testid={`clinician-${c.user_id}`}>
            <div className="flex items-start gap-3">
              <button onClick={()=>setEditFor(c)} className="relative group" title="Change photo"
                      data-testid={`edit-photo-${c.user_id}`}>
                {c.photo_url ? (
                  <img src={c.photo_url} alt={c.name} className="w-12 h-12 rounded-full object-cover border border-soft group-hover:opacity-70 transition"/>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#F2EBDB] flex items-center justify-center group-hover:bg-[#E8DFC8] transition">
                    <Stethoscope className="w-5 h-5 text-brand"/>
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-brand text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <Camera className="w-3 h-3"/>
                </span>
              </button>
              <div className="flex-1">
                <div className="font-heading font-semibold">{c.name}</div>
                <div className="text-xs text-fg-muted">{c.credentials || '—'}</div>
                <div className="text-xs text-fg-muted mt-1 truncate">{c.email}</div>
                {c.slug && (
                  <a href={`/p/${c.slug}`} target="_blank" rel="noopener noreferrer"
                     className="text-[10px] text-brand inline-flex items-center gap-0.5 mt-1 hover:underline"
                     data-testid={`public-link-${c.user_id}`}>
                    /p/{c.slug} ↗
                  </a>
                )}
              </div>
              {c.archived ? <span className="badge-neutral">Archived</span>
                : c.active ? <span className="badge-success">Active</span>
                : <span className="badge-warn">Inactive</span>}
            </div>
            {c.bio && <p className="text-xs text-fg-secondary mt-3">{c.bio}</p>}
            <div className="flex gap-2 mt-3 pt-3 border-t border-soft items-center flex-wrap">
              <button onClick={()=>setHoursFor(c)} className="text-xs text-brand inline-flex items-center gap-1" data-testid={`hours-${c.user_id}`}>
                <Clock className="w-3.5 h-3.5"/> Hours & blocks
              </button>
              <button onClick={()=>toggleApproval(c)}
                      className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${c.requires_approval ? 'bg-[#FFE0B3] text-[#8A4A00]' : 'bg-surface-2 text-fg-muted'}`}
                      title={c.requires_approval ? 'Patient self-bookings need admin approval' : 'Patient self-bookings auto-confirmed'}
                      data-testid={`approval-${c.user_id}`}>
                {c.requires_approval ? 'Approval' : 'Auto-confirm'}
              </button>
              <button onClick={()=>toggleActive(c)} className="text-xs text-fg-muted inline-flex items-center gap-1" data-testid={`toggle-${c.user_id}`}>
                <Power className="w-3.5 h-3.5"/> {c.active?'Off':'On'}
              </button>
              <button onClick={()=>archive(c)} className="text-xs text-fg-muted inline-flex items-center gap-1" data-testid={`archive-${c.user_id}`}>
                <Archive className="w-3.5 h-3.5"/> {c.archived?'Unarchive':'Archive'}
              </button>
              <button onClick={()=>remove(c)} className="text-xs text-[#B83A3A] inline-flex items-center gap-1 ml-auto" data-testid={`del-${c.user_id}`}>
                <Trash2 className="w-3.5 h-3.5"/>
              </button>
            </div>
          </div>
        ))}
        {!items.length && <div className="text-sm text-fg-muted">No clinicians yet. Add one.</div>}
      </div>
      {hoursFor && (
        <HoursModal clinician={hoursFor} onClose={()=>setHoursFor(null)} />
      )}
      {editFor && (
        <EditClinicianModal clinician={editFor} onClose={()=>{ setEditFor(null); load(); }}
                            onPhotoUpload={onPhotoUpload}/>
      )}
    </div>
  );
}
function F({ label, children, full }) {
  return <label className={full ? 'md:col-span-2 block' : 'block'}><div className="text-xs text-fg-muted mb-1">{label}</div>{children}</label>;
}

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export function HoursModal({ clinician, onClose }) {
  const [tab, setTab] = useState('schedule');
  const [days, setDays] = useState(() => Array(7).fill(null));
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlock, setNewBlock] = useState({ starts_at: '', ends_at: '', reason: '' });

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/clinicians/${clinician.user_id}/availability`),
      api.get(`/clinicians/${clinician.user_id}/blocks`),
    ]).then(([avail, blk]) => {
      if (!alive) return;
      const next = Array(7).fill(null);
      avail.data.forEach(w => {
        if (next[w.weekday] == null) {
          next[w.weekday] = {
            start_time: w.start_time, end_time: w.end_time,
            break_start: w.break_start || '', break_end: w.break_end || '',
            slot_step_min: w.slot_step_min || 15,
            slot_mode: (w.manual_slots && w.manual_slots.length) ? 'manual' : 'auto',
            manual_slots: (w.manual_slots || []).join(', '),
          };
        }
      });
      setDays(next);
      setBlocks(blk.data);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [clinician.user_id]);

  const setDay = (idx, patch) =>
    setDays(d => d.map((x, i) => i === idx ? { ...(x || defaults()), ...patch } : x));
  const toggleDay = (idx) =>
    setDays(d => d.map((x, i) => i === idx ? (x ? null : defaults()) : x));

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const windows = [];
      days.forEach((d, weekday) => {
        if (!d) return;
        const w = {
          weekday,
          start_time: d.start_time, end_time: d.end_time,
          break_start: d.break_start || null,
          break_end: d.break_end || null,
          slot_step_min: parseInt(d.slot_step_min, 10) || 15,
          manual_slots: null,
        };
        if (d.slot_mode === 'manual') {
          const list = (d.manual_slots || '').split(',').map(s => s.trim()).filter(s => /^\d{1,2}:\d{2}$/.test(s));
          w.manual_slots = list.length ? list : null;
        }
        windows.push(w);
      });
      await api.put(`/clinicians/${clinician.user_id}/availability`, { windows });
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const addBlock = async () => {
    if (!newBlock.starts_at || !newBlock.ends_at) return;
    try {
      await api.post(`/clinicians/${clinician.user_id}/blocks`, newBlock);
      const r = await api.get(`/clinicians/${clinician.user_id}/blocks`);
      setBlocks(r.data);
      setNewBlock({ starts_at: '', ends_at: '', reason: '' });
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const delBlock = async (block_id) => {
    if (!(await safeConfirm('Delete this block?'))) return;
    await api.delete(`/clinicians/${clinician.user_id}/blocks/${block_id}`);
    setBlocks(b => b.filter(x => x.block_id !== block_id));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="hours-modal">
      <div className="surface rounded-xl border border-soft w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-soft">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-accent font-medium">SCHEDULE & TIME OFF</div>
            <h3 className="font-heading font-semibold mt-0.5">{clinician.name}</h3>
          </div>
          <button onClick={onClose} className="text-fg-muted" data-testid="close-hours"><X className="w-5 h-5"/></button>
        </div>
        <div className="px-5 pt-3 flex gap-1 border-b border-soft">
          {['schedule', 'blocks'].map(t => (
            <button key={t} onClick={()=>setTab(t)} data-testid={`tab-${t}`}
                    className={`px-3 py-2 text-sm border-b-2 transition ${tab===t ? 'border-brand text-brand font-medium' : 'border-transparent text-fg-muted hover:text-fg'}`}>
              {t === 'schedule' ? 'Weekly schedule' : `Time off / blocks (${blocks.length})`}
            </button>
          ))}
        </div>

        {tab === 'schedule' && (
          <div className="p-5">
            {loading ? <div className="text-sm text-fg-muted">Loading…</div> : (
              <div className="space-y-3">
                {DAYS.map((d, idx) => {
                  const row = days[idx];
                  return (
                    <div key={d} className="border border-soft rounded-lg p-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="inline-flex items-center gap-2 w-20">
                          <input type="checkbox" checked={!!row} onChange={()=>toggleDay(idx)}
                                 data-testid={`day-${idx}-open`}/>
                          <span className="font-medium text-sm">{d}</span>
                        </label>
                        {row ? (
                          <>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-fg-muted">Hours</span>
                              <input type="time" value={row.start_time} onChange={e=>setDay(idx,{start_time:e.target.value})}
                                     className="input !py-1 !px-2 !text-xs w-24" data-testid={`day-${idx}-start`}/>
                              <span>–</span>
                              <input type="time" value={row.end_time} onChange={e=>setDay(idx,{end_time:e.target.value})}
                                     className="input !py-1 !px-2 !text-xs w-24" data-testid={`day-${idx}-end`}/>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-fg-muted">Lunch</span>
                              <input type="time" value={row.break_start} onChange={e=>setDay(idx,{break_start:e.target.value})}
                                     className="input !py-1 !px-2 !text-xs w-24" placeholder="—"/>
                              <span>–</span>
                              <input type="time" value={row.break_end} onChange={e=>setDay(idx,{break_end:e.target.value})}
                                     className="input !py-1 !px-2 !text-xs w-24" placeholder="—"/>
                            </div>
                          </>
                        ) : <span className="text-xs text-fg-muted">Closed</span>}
                      </div>
                      {row && (
                        <div className="mt-2.5 pt-2.5 border-t border-soft flex flex-wrap gap-3 items-center">
                          <label className="text-xs inline-flex items-center gap-2">
                            <input type="radio" name={`mode-${idx}`} checked={row.slot_mode==='auto'} onChange={()=>setDay(idx,{slot_mode:'auto'})}
                                   data-testid={`day-${idx}-auto`}/>
                            Auto every
                            <select value={row.slot_step_min} onChange={e=>setDay(idx,{slot_step_min:e.target.value})}
                                    disabled={row.slot_mode!=='auto'}
                                    className="input !py-0.5 !px-1 !text-xs"
                                    data-testid={`day-${idx}-step`}>
                              <option value={15}>15 min</option>
                              <option value={30}>30 min</option>
                              <option value={45}>45 min</option>
                              <option value={60}>60 min</option>
                            </select>
                          </label>
                          <label className="text-xs inline-flex items-center gap-2 flex-1 min-w-0">
                            <input type="radio" name={`mode-${idx}`} checked={row.slot_mode==='manual'} onChange={()=>setDay(idx,{slot_mode:'manual'})}
                                   data-testid={`day-${idx}-manual`}/>
                            Manual list
                            <input type="text" value={row.manual_slots} onChange={e=>setDay(idx,{manual_slots:e.target.value})}
                                   disabled={row.slot_mode!=='manual'}
                                   placeholder="10:00, 11:30, 14:00, 16:00"
                                   className="input !py-0.5 !px-2 !text-xs flex-1 min-w-32"
                                   data-testid={`day-${idx}-slots`}/>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'blocks' && (
          <div className="p-5 space-y-4">
            <div className="border border-dashed border-soft rounded-lg p-3 grid md:grid-cols-4 gap-2">
              <input type="datetime-local" value={newBlock.starts_at} onChange={e=>setNewBlock(b=>({...b,starts_at:e.target.value}))}
                     className="input text-xs" placeholder="From" data-testid="block-start"/>
              <input type="datetime-local" value={newBlock.ends_at} onChange={e=>setNewBlock(b=>({...b,ends_at:e.target.value}))}
                     className="input text-xs" placeholder="To" data-testid="block-end"/>
              <input type="text" value={newBlock.reason} onChange={e=>setNewBlock(b=>({...b,reason:e.target.value}))}
                     className="input text-xs" placeholder="Reason (optional — vacation, sick, training…)" data-testid="block-reason"/>
              <button onClick={addBlock} className="btn-primary text-xs" data-testid="block-add">+ Add block</button>
            </div>
            {blocks.length === 0 && <div className="text-sm text-fg-muted">No blocks yet. Use blocks to override your weekly schedule for vacations, training days, equipment downtime, etc.</div>}
            {blocks.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-xs text-fg-muted">
                  <tr><th className="text-left py-1">From</th><th className="text-left py-1">To</th><th className="text-left py-1">Reason</th><th></th></tr>
                </thead>
                <tbody>
                  {blocks.map(b => (
                    <tr key={b.block_id} className="border-t border-soft" data-testid={`block-row-${b.block_id}`}>
                      <td className="py-2">{new Date(b.starts_at).toLocaleString()}</td>
                      <td className="py-2">{new Date(b.ends_at).toLocaleString()}</td>
                      <td className="py-2 text-fg-muted">{b.reason || '—'}</td>
                      <td className="py-2 text-right">
                        <button onClick={()=>delBlock(b.block_id)} className="text-[#B83A3A] text-xs">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 p-5 border-t border-soft">
          <button onClick={onClose} className="btn-secondary text-sm">Close</button>
          {tab === 'schedule' && (
            <button onClick={saveSchedule} disabled={saving} className="btn-primary text-sm" data-testid="save-hours">
              {saving ? 'Saving…' : 'Save schedule'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function defaults() {
  return { start_time: '09:00', end_time: '17:00', break_start: '13:00', break_end: '13:30',
           slot_step_min: 15, slot_mode: 'auto', manual_slots: '' };
}

function EditClinicianModal({ clinician, onClose, onPhotoUpload }) {
  const [photo, setPhoto] = useState(clinician.photo_url || '');
  const [bio, setBio] = useState(clinician.bio || '');
  const [credentials, setCredentials] = useState(clinician.credentials || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clinicians/${clinician.user_id}`, {
        photo_url: photo || null, bio: bio || null, credentials: credentials || null,
      });
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="edit-clin-modal">
      <div className="surface rounded-xl border border-soft w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-soft">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-accent font-medium">PROFILE</div>
            <h3 className="font-heading font-semibold mt-0.5">{clinician.name}</h3>
          </div>
          <button onClick={onClose} className="text-fg-muted" data-testid="close-edit-clin"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4">
            {photo ? (
              <img src={photo} alt={clinician.name} className="w-24 h-24 rounded-full object-cover border-2 border-soft"/>
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#F2EBDB] flex items-center justify-center">
                <Camera className="w-6 h-6 text-fg-muted"/>
              </div>
            )}
            <div className="space-y-2">
              <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1.5" data-testid="edit-photo-btn">
                <Camera className="w-3.5 h-3.5"/> {photo ? 'Change photo' : 'Upload photo'}
                <input type="file" accept="image/*" className="hidden"
                       onChange={(e)=>onPhotoUpload(e, setPhoto)} data-testid="edit-photo-input"/>
              </label>
              {photo && (
                <button onClick={()=>setPhoto('')} type="button"
                        className="text-xs text-[#B83A3A] block" data-testid="edit-photo-clear">Remove photo</button>
              )}
            </div>
          </div>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Credentials</div>
            <input className="input" value={credentials} onChange={e=>setCredentials(e.target.value)}
                   data-testid="edit-clin-creds"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Bio</div>
            <textarea className="input min-h-[100px]" value={bio} onChange={e=>setBio(e.target.value)}
                      data-testid="edit-clin-bio"/>
          </label>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-soft">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="save-edit-clin">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
